const CDN = "https://cdn.xsolla.net/xsolla-ui-toolkit-prod/fonts/v1";

const UNICODE_RANGE_LATIN =
  "U+0000-024F, U+0250-02AF, U+0300-036F, U+0370-03FF, U+0400-04FF, U+2000-206F, U+20A0-20CF, U+2100-214F";
const UNICODE_RANGE_CN =
  "U+3000-303F, U+3400-4DBF, U+4E00-9FFF, U+F900-FAFF, U+FE30-FE4F, U+FF00-FFEF";
const UNICODE_RANGE_JP =
  "U+3000-303F, U+3040-309F, U+30A0-30FF, U+31F0-31FF, U+FF65-FF9F";
const UNICODE_RANGE_KR =
  "U+1100-11FF, U+3130-318F, U+A960-A97F, U+AC00-D7A3, U+D7B0-D7FF, U+FFA0-FFDC";

/**
 * @font-face CSS for all toolkit fonts, loaded from Xsolla CDN.
 *
 * Pilat Wide — B2C heading/display font, weights 600, 700, 800.
 * Pilat — B2B heading/display font, weights 300, 400, 500, 600, 700, 800.
 * Aktiv Grotesk — body font for all contexts, weights 400, 500, 600.
 *   Four editions loaded via unicode-range: Latin, CN/SG, JP, KR.
 *   The browser downloads only the subset needed for characters on the page.
 *
 * Weight coverage notes (intentional gaps — licensing constraint, Xsolla
 * does not hold rights to the omitted cuts):
 *   - Pilat Wide 400 — not licensed. Components using weight 400 on Pilat
 *     Wide will render at 600 via browser substitution (heavier).
 *   - Aktiv Grotesk 300 — not licensed. (No component currently requests it.)
 *   - Aktiv Grotesk 700 — not licensed. Substitutes to 600 (lighter).
 *   - CJK editions (cn-sg, jp, kr) — 400/500/600 only; no 700/800.
 *     Heavy weights on CJK text fall back to the Latin file (no glyphs)
 *     and then to the system fallback stack.
 *
 * Do not add @font-face entries for the omitted weights without first
 * confirming the licence has been extended.
 */
export const fontFacesCSS = `
  /* ── Pilat Wide (B2C headings) ── */

  @font-face {
    font-family: 'Pilat Wide';
    src: url('${CDN}/pilat-wide/pilat-wide-600.woff2') format('woff2');
    font-weight: 600;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'Pilat Wide';
    src: url('${CDN}/pilat-wide/pilat-wide-700.woff2') format('woff2');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'Pilat Wide';
    src: url('${CDN}/pilat-wide/pilat-wide-800.woff2') format('woff2');
    font-weight: 800;
    font-style: normal;
    font-display: swap;
  }

  /* ── Pilat (B2B headings) ── */

  @font-face {
    font-family: 'Pilat';
    src: url('${CDN}/pilat/pilat-300.woff2') format('woff2');
    font-weight: 300;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'Pilat';
    src: url('${CDN}/pilat/pilat-400.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'Pilat';
    src: url('${CDN}/pilat/pilat-500.woff2') format('woff2');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'Pilat';
    src: url('${CDN}/pilat/pilat-600.woff2') format('woff2');
    font-weight: 600;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'Pilat';
    src: url('${CDN}/pilat/pilat-700.woff2') format('woff2');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'Pilat';
    src: url('${CDN}/pilat/pilat-800.woff2') format('woff2');
    font-weight: 800;
    font-style: normal;
    font-display: swap;
  }

  /* ── Aktiv Grotesk (body) — Latin ── */

  @font-face {
    font-family: 'Aktiv Grotesk';
    src: url('${CDN}/aktiv-grotesk/latin/aktiv-grotesk-latin-400.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
    unicode-range: ${UNICODE_RANGE_LATIN};
  }
  @font-face {
    font-family: 'Aktiv Grotesk';
    src: url('${CDN}/aktiv-grotesk/latin/aktiv-grotesk-latin-500.woff2') format('woff2');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
    unicode-range: ${UNICODE_RANGE_LATIN};
  }
  @font-face {
    font-family: 'Aktiv Grotesk';
    src: url('${CDN}/aktiv-grotesk/latin/aktiv-grotesk-latin-600.woff2') format('woff2');
    font-weight: 600;
    font-style: normal;
    font-display: swap;
    unicode-range: ${UNICODE_RANGE_LATIN};
  }

  /* ── Aktiv Grotesk (body) — Chinese / SG ── */

  @font-face {
    font-family: 'Aktiv Grotesk';
    src: url('${CDN}/aktiv-grotesk/cn-sg/aktiv-grotesk-cn-sg-400.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
    unicode-range: ${UNICODE_RANGE_CN};
  }
  @font-face {
    font-family: 'Aktiv Grotesk';
    src: url('${CDN}/aktiv-grotesk/cn-sg/aktiv-grotesk-cn-sg-500.woff2') format('woff2');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
    unicode-range: ${UNICODE_RANGE_CN};
  }
  @font-face {
    font-family: 'Aktiv Grotesk';
    src: url('${CDN}/aktiv-grotesk/cn-sg/aktiv-grotesk-cn-sg-600.woff2') format('woff2');
    font-weight: 600;
    font-style: normal;
    font-display: swap;
    unicode-range: ${UNICODE_RANGE_CN};
  }

  /* ── Aktiv Grotesk (body) — Japanese ── */

  @font-face {
    font-family: 'Aktiv Grotesk';
    src: url('${CDN}/aktiv-grotesk/jp/aktiv-grotesk-jp-400.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
    unicode-range: ${UNICODE_RANGE_JP};
  }
  @font-face {
    font-family: 'Aktiv Grotesk';
    src: url('${CDN}/aktiv-grotesk/jp/aktiv-grotesk-jp-500.woff2') format('woff2');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
    unicode-range: ${UNICODE_RANGE_JP};
  }
  @font-face {
    font-family: 'Aktiv Grotesk';
    src: url('${CDN}/aktiv-grotesk/jp/aktiv-grotesk-jp-600.woff2') format('woff2');
    font-weight: 600;
    font-style: normal;
    font-display: swap;
    unicode-range: ${UNICODE_RANGE_JP};
  }

  /* ── Aktiv Grotesk (body) — Korean ── */

  @font-face {
    font-family: 'Aktiv Grotesk';
    src: url('${CDN}/aktiv-grotesk/kr/aktiv-grotesk-kr-400.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
    unicode-range: ${UNICODE_RANGE_KR};
  }
  @font-face {
    font-family: 'Aktiv Grotesk';
    src: url('${CDN}/aktiv-grotesk/kr/aktiv-grotesk-kr-500.woff2') format('woff2');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
    unicode-range: ${UNICODE_RANGE_KR};
  }
  @font-face {
    font-family: 'Aktiv Grotesk';
    src: url('${CDN}/aktiv-grotesk/kr/aktiv-grotesk-kr-600.woff2') format('woff2');
    font-weight: 600;
    font-style: normal;
    font-display: swap;
    unicode-range: ${UNICODE_RANGE_KR};
  }
`;
